/**
 * React Query hooks for study-centre learner pathways (Phase SC-3b).
 *
 * Query keys are namespaced under `["learning", "pathways", ...]` so the
 * learning surface invalidates cleanly without touching other portal
 * caches. Mutations invalidate the list on success so the hub reflects
 * new / paused / archived / deleted pathways immediately.
 */
import {
  keepPreviousData,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { MY_ENROLLMENT_KEY } from "../enrollment/api";
import { coachApi, learningApi } from "./api";
import { mergeStudyGroupMessageInCache } from "./studyGroupRealtime";
import type {
  CreatePathwayInput,
  CreatePracticeTestInput,
  CreateReadLogInput,
  CreateSkillAssessmentInput,
  CreateStudyGroupInput,
  DiagnosticAnswer,
  PathwayStatus,
  StartDiagnosticInput,
  StartPostReviewQuizInput,
  UpdatePathwayInput,
} from "./types";

const KEY = {
  root: ["learning", "pathways"] as const,
  list: (status?: PathwayStatus) => ["learning", "pathways", "list", status ?? "all"] as const,
  detail: (id: string) => ["learning", "pathways", "detail", id] as const,
};

export const COACH_KEY = {
  root: ["learning", "coach"] as const,
  summary: ["learning", "coach", "summary"] as const,
  due: ["learning", "coach", "due"] as const,
  readLogs: (pathwayId?: string) => ["learning", "coach", "read-logs", pathwayId ?? "all"] as const,
  trackRecord: (pathwayId?: string) =>
    ["learning", "coach", "track-record", pathwayId ?? "all"] as const,
  trackRecordDetail: (entryId: string) =>
    ["learning", "coach", "track-record-detail", entryId] as const,
  learningPreferences: ["learning", "coach", "learning-preferences"] as const,
  progressReports: (pathwayId?: string) =>
    ["learning", "coach", "progress-reports", pathwayId ?? "all"] as const,
  notifications: ["learning", "coach", "notifications"] as const,
  skillCertificates: ["learning", "coach", "skill-certificates"] as const,
  studyGroups: (pathwayId?: string) =>
    ["learning", "coach", "study-groups", pathwayId ?? "all"] as const,
  studyGroupMessages: (groupId: string) =>
    ["learning", "coach", "study-group-messages", groupId] as const,
  studyGroupMembers: (groupId: string) =>
    ["learning", "coach", "study-group-members", groupId] as const,
  studyGroupMemberStats: (groupId: string, memberId: string) =>
    ["learning", "coach", "study-group-member-stats", groupId, memberId] as const,
  studyGroupMessageReceipts: (groupId: string, messageId: string) =>
    ["learning", "coach", "study-group-receipts", groupId, messageId] as const,
  studyGroupChatProfile: (groupId: string) =>
    ["learning", "coach", "study-group-chat-profile", groupId] as const,
};

export function usePathways(status?: PathwayStatus, enabled = true) {
  return useQuery({
    queryKey: KEY.list(status),
    queryFn: () => learningApi.list(status ? { status } : undefined),
    enabled,
  });
}

export function usePathway(id: string | null) {
  return useQuery({
    queryKey: KEY.detail(id ?? "none"),
    queryFn: () => learningApi.get(id as string),
    enabled: !!id,
  });
}

const COURSE_NAV_KEY = (pathwayId: string) => ["learning", "course-navigation", pathwayId] as const;

export function useCourseNavigation(pathwayId: string | null, enabled = true) {
  return useQuery({
    queryKey: COURSE_NAV_KEY(pathwayId ?? "none"),
    queryFn: () => learningApi.getCourseNavigation(pathwayId as string),
    enabled: enabled && !!pathwayId,
  });
}

export function useCompleteCourseLeaf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pathwayId, leafId }: { pathwayId: string; leafId: string }) =>
      learningApi.completeCourseLeaf(pathwayId, leafId),
    onSuccess: (result) => {
      qc.setQueryData(COURSE_NAV_KEY(result.pathway_id), result);
      qc.invalidateQueries({ queryKey: COACH_KEY.summary });
    },
  });
}

/**
 * Shared key so an in-flight generation started on one screen (e.g. the
 * intake wizard) stays observable after that screen unmounts — the plan
 * page shows a "building your roadmap" state instead of a dead end.
 */
export const GENERATE_ROADMAP_MUTATION_KEY = ["learning", "generate-roadmap"] as const;

export function useGenerateRoadmap() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: GENERATE_ROADMAP_MUTATION_KEY,
    mutationFn: ({ id, regenerate }: { id: string; regenerate?: boolean }) =>
      learningApi.generateRoadmap(id, regenerate),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: KEY.root });
      qc.invalidateQueries({ queryKey: KEY.detail(result.pathway_id) });
      qc.invalidateQueries({ queryKey: COURSE_NAV_KEY(result.pathway_id) });
    },
  });
}

/**
 * Observe roadmap generation for `pathwayId` across component boundaries.
 *
 * Generation is fired from the intake wizard and finishes long after that
 * wizard unmounts, so neither the in-flight nor the failed state can live on
 * a local mutation result — otherwise a background failure would silently
 * reset the plan page to its "Generate my roadmap" call to action.
 */
export function useRoadmapGenerationState(pathwayId: string | null): {
  generating: boolean;
  failed: boolean;
} {
  const forThisPathway = (variables: unknown) =>
    pathwayId !== null && (variables as { id?: string } | undefined)?.id === pathwayId;

  const pending = useMutationState({
    filters: { mutationKey: GENERATE_ROADMAP_MUTATION_KEY, status: "pending" },
    select: (mutation) => forThisPathway(mutation.state.variables),
  });
  const errored = useMutationState({
    filters: { mutationKey: GENERATE_ROADMAP_MUTATION_KEY, status: "error" },
    select: (mutation) => forThisPathway(mutation.state.variables),
  });

  const generating = pending.some(Boolean);
  // A retry supersedes the previous failure.
  return { generating, failed: !generating && errored.some(Boolean) };
}

/** True while a roadmap generation for `pathwayId` is still in flight. */
export function useIsRoadmapGenerating(pathwayId: string | null): boolean {
  return useRoadmapGenerationState(pathwayId).generating;
}

export function useCreatePathway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePathwayInput) => learningApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY.root });
      qc.invalidateQueries({ queryKey: MY_ENROLLMENT_KEY });
    },
  });
}

export function useUpdatePathway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePathwayInput }) =>
      learningApi.update(id, body),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: KEY.root });
      qc.invalidateQueries({ queryKey: MY_ENROLLMENT_KEY });
      qc.setQueryData(KEY.detail(updated.id), updated);
    },
  });
}

export function useDeletePathway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => learningApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY.root });
      // Deleting a plan removes its cards, so the coach counters are stale.
      qc.invalidateQueries({ queryKey: COACH_KEY.root });
    },
  });
}

/* ── Daily loop (Phase SC-3d) ─────────────────────────────────────────── */

export function useCoachSummary(enabled = true, pathwayId?: string | null) {
  const scope = pathwayId ?? "all";
  return useQuery({
    queryKey: [...COACH_KEY.summary, scope],
    queryFn: () => coachApi.summary(pathwayId),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useDueCards(enabled = true, pathwayId?: string | null) {
  const scope = pathwayId ?? "all";
  return useQuery({
    queryKey: [...COACH_KEY.due, scope],
    queryFn: () => coachApi.dueCards(undefined, pathwayId),
    enabled,
  });
}

/**
 * Starting a diagnostic is a mutation, not a query, even though it can
 * return an existing session: it may generate one, which costs an AI
 * call. Modelling it as a query would let React Query retry or refetch it
 * on focus and quietly spend the tenant's budget.
 */
export function useStartDiagnostic() {
  return useMutation({
    mutationFn: ({ pathwayId, body }: { pathwayId: string; body?: StartDiagnosticInput }) =>
      coachApi.startDiagnostic(pathwayId, body),
  });
}

export function useLearningPreferences(enabled = true) {
  return useQuery({
    queryKey: COACH_KEY.learningPreferences,
    queryFn: () => coachApi.learningPreferences(),
    enabled,
    staleTime: 60_000,
  });
}

export function useTrackRecordDetail(entryId: string | null, enabled = true) {
  return useQuery({
    queryKey: COACH_KEY.trackRecordDetail(entryId ?? ""),
    queryFn: () => coachApi.trackRecordDetail(entryId as string),
    enabled: enabled && Boolean(entryId),
  });
}

export function useSubmitDiagnostic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, answers }: { sessionId: string; answers: DiagnosticAnswer[] }) =>
      coachApi.submitDiagnostic(sessionId, answers),
    // Submitting seeds cards, so both the queue and the header move.
    onSuccess: () => qc.invalidateQueries({ queryKey: COACH_KEY.root }),
  });
}

export function useReviewCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, quality }: { cardId: string; quality: number }) =>
      coachApi.reviewCard(cardId, quality),
    onSuccess: () => qc.invalidateQueries({ queryKey: COACH_KEY.root }),
  });
}

export function useStartPostReviewQuiz() {
  return useMutation({
    mutationFn: ({ cardId, body }: { cardId: string; body?: StartPostReviewQuizInput }) =>
      coachApi.startPostReviewQuiz(cardId, body),
  });
}

export function useSubmitPostReviewQuiz() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, answers }: { sessionId: string; answers: DiagnosticAnswer[] }) =>
      coachApi.submitPostReviewQuiz(sessionId, answers),
    onSuccess: () => qc.invalidateQueries({ queryKey: COACH_KEY.root }),
  });
}

/** SC-10 — log what you read; seeds the Ebbinghaus/SM-2 schedule. */
export function useLogReading() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pathwayId, body }: { pathwayId: string; body: CreateReadLogInput }) =>
      coachApi.logReading(pathwayId, body),
    onSuccess: (_data, { pathwayId }) => {
      void qc.invalidateQueries({ queryKey: COACH_KEY.root });
      void qc.invalidateQueries({ queryKey: COURSE_NAV_KEY(pathwayId) });
    },
  });
}

export function useReadLogs(pathwayId?: string, enabled = true) {
  return useQuery({
    queryKey: COACH_KEY.readLogs(pathwayId),
    queryFn: () => coachApi.readLogs(pathwayId ? { pathway_id: pathwayId } : undefined),
    enabled,
  });
}

export function useStartPracticeTest() {
  return useMutation({
    mutationFn: ({ pathwayId, body }: { pathwayId: string; body: CreatePracticeTestInput }) =>
      coachApi.startPracticeTest(pathwayId, body),
  });
}

export function useSubmitPracticeTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, answers }: { sessionId: string; answers: DiagnosticAnswer[] }) =>
      coachApi.submitPracticeTest(sessionId, answers),
    onSuccess: () => qc.invalidateQueries({ queryKey: COACH_KEY.root }),
  });
}

export function useTrackRecord(pathwayId?: string, enabled = true) {
  return useQuery({
    queryKey: COACH_KEY.trackRecord(pathwayId),
    queryFn: () => coachApi.trackRecord(pathwayId ? { pathway_id: pathwayId } : undefined),
    enabled,
  });
}

/** Home mini-chart. Latest 100 track-record rows, all pathways. */
export function useHomeActivity(enabled: boolean) {
  return useQuery({
    queryKey: [...COACH_KEY.trackRecord(), "home-week"] as const,
    queryFn: () => coachApi.trackRecord({ limit: 100 }),
    enabled,
    staleTime: 30_000,
  });
}

export function useProgressReports(pathwayId?: string, enabled = true) {
  return useQuery({
    queryKey: COACH_KEY.progressReports(pathwayId),
    queryFn: () => coachApi.progressReports(pathwayId ? { pathway_id: pathwayId } : undefined),
    enabled,
  });
}

export function useGenerateProgressReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pathwayId, periodDays }: { pathwayId: string; periodDays?: number }) =>
      coachApi.generateProgressReport(pathwayId, { period_days: periodDays }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COACH_KEY.root });
      qc.invalidateQueries({ queryKey: COACH_KEY.progressReports() });
      qc.invalidateQueries({ queryKey: COACH_KEY.notifications });
    },
  });
}

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: COACH_KEY.notifications,
    queryFn: () => coachApi.notifications(),
    enabled,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => coachApi.markNotificationRead(notificationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: COACH_KEY.root }),
  });
}

export function useSkillCertificates(enabled = true) {
  return useQuery({
    queryKey: COACH_KEY.skillCertificates,
    queryFn: () => coachApi.skillCertificates(),
    enabled,
  });
}

export function useStartSkillAssessment() {
  return useMutation({
    mutationFn: ({ pathwayId, body }: { pathwayId: string; body: CreateSkillAssessmentInput }) =>
      coachApi.startSkillAssessment(pathwayId, body),
  });
}

export function useSubmitSkillAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, answers }: { sessionId: string; answers: DiagnosticAnswer[] }) =>
      coachApi.submitSkillAssessment(sessionId, answers),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COACH_KEY.skillCertificates });
    },
  });
}

export function useStudyGroups(pathwayId?: string, enabled = true) {
  return useQuery({
    queryKey: COACH_KEY.studyGroups(pathwayId),
    queryFn: () => coachApi.studyGroups(pathwayId ? { pathway_id: pathwayId } : undefined),
    enabled,
  });
}

export function useCreateStudyGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateStudyGroupInput) => coachApi.createStudyGroup(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: COACH_KEY.studyGroups() }),
  });
}

export function useJoinStudyGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => coachApi.joinStudyGroup(groupId),
    onSuccess: () => qc.invalidateQueries({ queryKey: COACH_KEY.studyGroups() }),
  });
}

export function useJoinStudyGroupByInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteCode: string) => coachApi.joinStudyGroupByInvite(inviteCode),
    onSuccess: () => qc.invalidateQueries({ queryKey: COACH_KEY.studyGroups() }),
  });
}

export function useStudyGroupMessages(
  groupId: string | null,
  enabled = true,
  pollMs: number | false = false,
) {
  return useQuery({
    queryKey: COACH_KEY.studyGroupMessages(groupId ?? ""),
    queryFn: () => coachApi.studyGroupMessages(groupId!),
    enabled: enabled && Boolean(groupId),
    placeholderData: keepPreviousData,
    refetchInterval: pollMs,
  });
}

export function usePostStudyGroupMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      groupId,
      body,
      mention_user_ids,
      client_message_id,
    }: {
      groupId: string;
      body: string;
      mention_user_ids?: string[];
      client_message_id: string;
    }) =>
      coachApi.postStudyGroupMessage(groupId, {
        body,
        mention_user_ids,
        client_message_id,
      }),
    onSuccess: (data, vars) => {
      mergeStudyGroupMessageInCache(qc, vars.groupId, data);
    },
  });
}

export function useStudyGroupMembers(groupId: string | null, enabled = true) {
  return useQuery({
    queryKey: COACH_KEY.studyGroupMembers(groupId ?? ""),
    queryFn: () => coachApi.studyGroupMembers(groupId!),
    enabled: enabled && Boolean(groupId),
  });
}

export function useStudyGroupMemberStats(
  groupId: string | null,
  memberUserId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: COACH_KEY.studyGroupMemberStats(groupId ?? "", memberUserId ?? ""),
    queryFn: () => coachApi.studyGroupMemberStats(groupId!, memberUserId!),
    enabled: enabled && Boolean(groupId && memberUserId),
  });
}

export function useBanStudyGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      coachApi.banStudyGroupMember(groupId, userId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroupMembers(vars.groupId) });
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroups() });
    },
  });
}

export function useRemoveStudyGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      coachApi.removeStudyGroupMember(groupId, userId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroupMembers(vars.groupId) });
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroups() });
    },
  });
}

export function useUpdateStudyGroupInviteSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      groupId,
      invite_link_mode,
      regenerate_code,
    }: {
      groupId: string;
      invite_link_mode: "permanent" | "single_use";
      regenerate_code?: boolean;
    }) => coachApi.updateStudyGroupInviteSettings(groupId, { invite_link_mode, regenerate_code }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COACH_KEY.studyGroups() }),
  });
}

export function usePostStudyGroupImageMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      groupId,
      image_url,
      caption,
      mention_user_ids,
      client_message_id,
    }: {
      groupId: string;
      image_url: string;
      caption?: string;
      mention_user_ids?: string[];
      client_message_id: string;
    }) =>
      coachApi.postStudyGroupImageMessage(groupId, {
        image_url,
        caption,
        mention_user_ids,
        client_message_id,
      }),
    onSuccess: (data, vars) => {
      mergeStudyGroupMessageInCache(qc, vars.groupId, data);
    },
  });
}

export function useDeleteStudyGroupMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, messageId }: { groupId: string; messageId: string }) =>
      coachApi.deleteStudyGroupMessage(groupId, messageId),
    onSuccess: (data, vars) => {
      mergeStudyGroupMessageInCache(qc, vars.groupId, data);
    },
  });
}

export function useStudyGroupChatProfile(groupId: string | null, enabled = true) {
  return useQuery({
    queryKey: COACH_KEY.studyGroupChatProfile(groupId ?? ""),
    queryFn: () => coachApi.studyGroupChatProfile(groupId!),
    enabled: enabled && Boolean(groupId),
  });
}

export function useSetStudyGroupChatProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, chat_username }: { groupId: string; chat_username: string }) =>
      coachApi.setStudyGroupChatProfile(groupId, { chat_username }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroupChatProfile(vars.groupId) });
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroupMembers(vars.groupId) });
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroups() });
    },
  });
}

export function useStudyGroupMessageReceipts(
  groupId: string | null,
  messageId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: COACH_KEY.studyGroupMessageReceipts(groupId ?? "", messageId ?? ""),
    queryFn: () => coachApi.studyGroupMessageReceipts(groupId!, messageId!),
    enabled: enabled && Boolean(groupId && messageId),
  });
}

export function usePromoteStudyGroupModerator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      coachApi.promoteStudyGroupModerator(groupId, userId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroupMembers(vars.groupId) });
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroups() });
    },
  });
}

export function useDemoteStudyGroupModerator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      coachApi.demoteStudyGroupModerator(groupId, userId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroupMembers(vars.groupId) });
      qc.invalidateQueries({ queryKey: COACH_KEY.studyGroups() });
    },
  });
}
